import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiArrowUpRight, FiMonitor, FiSmartphone } from "react-icons/fi";
import { BrowserFrame, PhoneFrame } from "../components/ui/DeviceFrames";
import Lightbox from "../components/ui/Lightbox";
import Magnetic from "../components/ui/Magnetic";
import { useIntro } from "../context/IntroContext";
import { getProject, projects } from "../data/projects";
import { ease, fadeUp, staggerChildren, trackSpotlight, viewportOnce } from "../utils/motionPresets";
import "./project-page.css";

const pad = (n) => String(n).padStart(2, "0");

const Reveal = ({ as = "section", className, children }) => {
  const Component = motion[as];
  return (
    <Component
      className={className}
      variants={staggerChildren}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
    >
      {children}
    </Component>
  );
};

const StoryRow = ({ label, children }) => (
  <motion.div className="case__row" variants={fadeUp}>
    <h2 className="case__row-label mono">{label}</h2>
    <div className="case__row-body">{children}</div>
  </motion.div>
);

const ProjectPage = () => {
  const { id } = useParams();
  const project = getProject(id);
  const { ready } = useIntro();
  const [lightbox, setLightbox] = useState(null);
  const openImage = useCallback((src, alt) => setLightbox({ src, alt }), []);
  const closeImage = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    if (project) document.title = `${project.title} — Case study · Yaseen Al-Midani`;
  }, [project]);

  if (!project) return <Navigate to="/#work" replace />;

  const index = projects.indexOf(project);
  const next = projects[(index + 1) % projects.length];
  const { desktop, mobile } = project.gallery;

  const meta = [
    { label: "Role", value: project.role },
    { label: "Context", value: project.context },
    { label: "Sector", value: project.sector },
    { label: "Market", value: project.market }
  ];

  return (
    <article className="case" key={project.id}>
      <header className="container case__head">
        <motion.div
          className="case__crumbs"
          initial={{ opacity: 0, y: 12 }}
          animate={ready ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.6, ease }}
        >
          <Link to="/#work" className="case__back">
            <FiArrowLeft /> All work
          </Link>
          <span className="mono case__count">
            {pad(index + 1)} / {pad(projects.length)}
          </span>
        </motion.div>

        <motion.span
          className="eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={ready ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.7, ease, delay: 0.05 }}
        >
          <span className="eyebrow__index">Case study</span>
          {project.category}
        </motion.span>

        <h1 className="case__title">
          <motion.span
            className="case__title-inner"
            initial={{ y: "105%" }}
            animate={ready ? { y: "0%" } : undefined}
            transition={{ duration: 1, ease, delay: 0.1 }}
          >
            {project.title}
          </motion.span>
        </h1>

        <motion.div
          className="case__intro"
          initial={{ opacity: 0, y: 20 }}
          animate={ready ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.8, ease, delay: 0.3 }}
        >
          <p className="case__tagline">{project.tagline}</p>
          <div className="case__actions">
            <Magnetic strength={0.2}>
              <a href={project.link} target="_blank" rel="noreferrer" className="btn btn--primary">
                Visit live site <FiArrowUpRight />
              </a>
            </Magnetic>
            <Link to={`/work/${next.id}`} className="btn btn--ghost">
              Next project <FiArrowRight />
            </Link>
          </div>
        </motion.div>

        <motion.dl
          className="case__meta"
          initial={{ opacity: 0 }}
          animate={ready ? { opacity: 1 } : undefined}
          transition={{ duration: 0.8, delay: 0.45 }}
        >
          {meta.map((item) => (
            <div key={item.label} className="case__meta-item">
              <dt className="mono">{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </motion.dl>
      </header>

      <motion.div
        className={`container case__showcase ${mobile.length ? "has-phone" : ""}`}
        initial={{ opacity: 0, y: 50 }}
        animate={ready ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 1.1, ease, delay: 0.35 }}
      >
        <BrowserFrame
          src={desktop[0]}
          url={project.link}
          alt={`${project.title} — desktop homepage`}
          onOpen={openImage}
          eager
        />
        {mobile[0] && (
          <PhoneFrame
            src={mobile[0]}
            alt={`${project.title} — mobile homepage`}
            onOpen={openImage}
            className="case__showcase-phone"
          />
        )}
      </motion.div>

      {project.facts && (
        <Reveal className="container case__facts">
          {project.facts.map((fact) => (
            <motion.div key={fact.label} className="case__fact" variants={fadeUp}>
              <span className="case__fact-value">{fact.value}</span>
              <span className="case__fact-label">{fact.label}</span>
            </motion.div>
          ))}
          {project.factsNote && (
            <motion.p className="case__facts-note mono" variants={fadeUp}>
              {project.factsNote}
            </motion.p>
          )}
        </Reveal>
      )}

      <Reveal className="container case__story">
        <StoryRow label="Overview">
          {project.overview.map((paragraph) => (
            <p key={paragraph.slice(0, 24)}>{paragraph}</p>
          ))}
        </StoryRow>
        {project.contribution && (
          <StoryRow label="My contribution">
            <p>{project.contribution}</p>
          </StoryRow>
        )}
        <StoryRow label="The brief">
          <p>{project.brief}</p>
        </StoryRow>
        <StoryRow label="My approach">
          <p>{project.approach}</p>
        </StoryRow>
      </Reveal>

      <section className="container case__section">
        <Reveal as="div" className="case__section-head">
          <motion.span className="eyebrow" variants={fadeUp}>
            <span className="eyebrow__index">01</span>
            Key features
          </motion.span>
          <motion.h2 className="case__section-title" variants={fadeUp}>
            What I <span className="serif">built.</span>
          </motion.h2>
        </Reveal>
        <Reveal as="div" className="case__features">
          {project.features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="card case__feature"
              variants={fadeUp}
              onPointerMove={trackSpotlight}
            >
              <span className="case__feature-index mono">{pad(i + 1)}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </motion.div>
          ))}
        </Reveal>
      </section>

      {desktop.length > 1 && (
        <section className="container case__section">
          <Reveal as="div" className="case__section-head">
            <motion.span className="eyebrow" variants={fadeUp}>
              <span className="eyebrow__index">
                <FiMonitor />
              </span>
              Desktop
            </motion.span>
            <motion.h2 className="case__section-title" variants={fadeUp}>
              On the <span className="serif">big screen.</span>
            </motion.h2>
          </Reveal>
          <div className="case__desktop">
            {desktop.slice(1).map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, ease }}
              >
                <BrowserFrame
                  src={src}
                  url={project.link}
                  alt={`${project.title} — desktop section ${i + 2}`}
                  onOpen={openImage}
                />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {mobile.length > 0 && (
        <section className="case__section case__mobile-section">
          <div className="container">
            <Reveal as="div" className="case__section-head case__section-head--split">
              <div>
                <motion.span className="eyebrow" variants={fadeUp}>
                  <span className="eyebrow__index">
                    <FiSmartphone />
                  </span>
                  Mobile
                </motion.span>
                <motion.h2 className="case__section-title" variants={fadeUp}>
                  Designed for <span className="serif">phones.</span>
                </motion.h2>
              </div>
              {project.mobileNote && (
                <motion.p className="section-lede" variants={fadeUp}>
                  {project.mobileNote}
                </motion.p>
              )}
            </Reveal>
          </div>
          <Reveal as="div" className="case__phones">
            {mobile.map((src, i) => (
              <motion.div key={src} variants={fadeUp} className="case__phone-slot">
                <PhoneFrame src={src} alt={`${project.title} — mobile screen ${i + 1}`} onOpen={openImage} />
              </motion.div>
            ))}
          </Reveal>
        </section>
      )}

      <section className="container case__section">
        <Reveal as="div" className="case__section-head">
          <motion.span className="eyebrow" variants={fadeUp}>
            <span className="eyebrow__index">02</span>
            Tech stack
          </motion.span>
          <motion.h2 className="case__section-title" variants={fadeUp}>
            Under the <span className="serif">hood.</span>
          </motion.h2>
        </Reveal>
        <Reveal as="ul" className="case__stack">
          {project.stack.map((tech) => (
            <motion.li key={tech.name} className="case__stack-item" variants={fadeUp}>
              <span className="case__stack-name">{tech.name}</span>
              <span className="case__stack-use">{tech.use}</span>
            </motion.li>
          ))}
        </Reveal>
        {project.note && <p className="case__note mono">{project.note}</p>}
      </section>

      <section className="container case__next">
        <Link to={`/work/${next.id}`} className="card case__next-card" onPointerMove={trackSpotlight}>
          <div className="case__next-text">
            <span className="eyebrow">
              <span className="eyebrow__index">Next project</span>
              {next.category}
            </span>
            <span className="case__next-title">
              {next.title} <FiArrowRight />
            </span>
            <span className="case__next-tagline">{next.summary}</span>
          </div>
          <div className="case__next-media">
            <img src={next.gallery.desktop[0]} alt="" loading="lazy" />
          </div>
        </Link>
      </section>

      <Lightbox image={lightbox} onClose={closeImage} />
    </article>
  );
};

export default ProjectPage;
